import {
  Box,
  IconButton,
  InputBase,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FieldError } from "react-hook-form";
import { InvoiceOverviewInfo } from "../../../types";
import { formatCurrency, formatDate } from "../../../utils";

type Props = {
  invoice: InvoiceOverviewInfo | undefined;
  amountValue: number | undefined;
  error?: FieldError;
  onAmountChange: (value: number | undefined) => void;
  onClose: () => void;
  disableAmount?: boolean;
};
export const InvoiceSummaryPanel = ({
  invoice,
  amountValue,
  error,
  onAmountChange,
  onClose,
  disableAmount = false,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const miscTotal =
    (invoice?.totalMiscCharge ?? 0) + (invoice?.tripMinimumCharge ?? 0);
  const enteredAmount = amountValue ?? 0;
  const isAmountExceeded = (amountValue ?? 0) > (invoice?.balanceAmount ?? 0);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 320 },
        height: { xs: "auto", md: "100%" },
        boxSizing: "border-box",
        flexShrink: 0,
        minHeight: 0,
        bgcolor: theme.palette.secondary.main,
        color: "#fff",

        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, md: 2.5 },

        p: { xs: "20px 16px", sm: "24px 20px", md: "28px 24px" },

        overflowY: { xs: "visible", md: "auto" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#7c8fbe",
              }}
            >
              Invoice Summary
            </Typography>
            {isMobile && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.1)",
                  p: 0.5,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Typography sx={{ fontSize: 13, color: "#7c8fbe", mt: 1.25 }}>
            Amount
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
              bgcolor: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "8px",
              px: 1.25,
              py: 0.75,
            }}
          >
            <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700 }}>
              $
            </Typography>
            <InputBase
              value={amountValue ?? ""}
              type="number"
              disabled={disableAmount}
              onChange={(e) => {
                const value =
                  e.target.value === "" ? undefined : Number(e.target.value);

                onAmountChange(value);
              }}
              sx={{
                width: "100%",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: 20, sm: 22 },
                "& input": { p: 0, MozAppearance: "textfield" },
                "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button":
                  {
                    WebkitAppearance: "none",
                  },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#B0B0B0",
                  WebkitTextFillColor: "#B0B0B0",
                  opacity: 1,
                },
              }}
            />
          </Box>

          {isAmountExceeded && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: "#FF6B6B",
                fontWeight: 500,
              }}
            >
              Payment amount cannot exceed the outstanding balance.
            </Typography>
          )}
          {error && !isAmountExceeded && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: "#FF6B6B",
                fontWeight: 500,
              }}
            >
              {error.message}
            </Typography>
          )}
          {!!invoice?.pendingAchAmount && invoice.pendingAchAmount > 0 && (
            <Typography
              sx={{ mt: 1, fontSize: 12, color: "#F5A623", fontWeight: 500 }}
            >
              {formatCurrency(invoice.pendingAchAmount)} pending ACH settlement
              (typically clears in 4–5 business days)
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "rgba(255,255,255,0.08)",
          borderRadius: "12px",
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Row label="Invoice Number" value={invoice?.invoiceNumber ?? "-"} />

        <Row
          label="Invoice Date"
          value={
            invoice?.invoiceDate
              ? formatDate(invoice.invoiceDate, "MMMM D, YYYY")
              : "-"
          }
        />

        <Row
          label="Due Date"
          value={
            invoice?.dueDate ? formatDate(invoice.dueDate, "MMMM D, YYYY") : "-"
          }
        />
      </Box>

      <Box
        sx={{
          bgcolor: "rgba(255,255,255,0.08)",
          borderRadius: "12px",
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Row
          label="Wash Charges"
          value={formatCurrency(invoice?.totalWashCharge || 0)}
        />

        <Row label="Miscellaneous Charges" value={formatCurrency(miscTotal)} />

        <Row
          label="Invoice Amount"
          value={formatCurrency(invoice?.invoiceAmount || 0)}
        />

        <Row
          label="Payment Amount"
          value={formatCurrency(enteredAmount)}
          isTotal
        />
      </Box>
    </Box>
  );
};

function Row({
  label,
  value,
  isTotal = false,
}: {
  label: string;
  value: string;
  isTotal?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.5,
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: isTotal ? 13 : 12,
          fontWeight: isTotal ? 700 : 500,
          color: isTotal ? "#fff" : "#9fb0d1",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: isTotal ? 16 : 13,
          fontWeight: isTotal ? 700 : 600,
          color: "#fff",
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
